import React, { useState , useRef, useEffect } from 'react';
import { API } from "aws-amplify";
import Fuse from 'fuse.js';

import DepartmentProgressScreen from '../department_progress_screen';
import EditCourseData from '../../modals/edit_course';
import SupervisorSelector from '../reusable-components/SupervisorSelector';
import SupervisorPill from '../reusable-components/SupervisorPill';
import Dropdown from '../../../Dropdown';
import Button from '../../../Button';
import SearchAddButton from '../../../SearchAddButton';
import useDepartmentResourceFeedback from './useDepartmentResourceFeedback';

import { capitalizeFirstLetters, useOutsideClick, getData } from '../../../../js/Helpers';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLock, faUserCircle, faChalkboardTeacher, faBook, faHourglassHalf, faCheckCircle, faCheck, faSearch, faTimes, faExclamationCircle, faTrashAlt, faChevronLeft } from '@fortawesome/free-solid-svg-icons';

export default function CleanCourseTeachers({isLoading, subscreenRestricted, subscreenLocked, subscreen, subscreenResponsibleRoleID, supervisors, openSupervisorsList, closeSupervisorsList, addSupervisorToDepartment, removeSupervisorFromDepartment, users, userIsSupervisor, userIsAdmin, departments, setDepartments, departmentID, sortedDepartments, changeDepartment, userInfo, schoolInfo, schoolType, scheduleInfo, courses, setCourses, teachers, classrooms, students, setStudents, sections, labels, scheduleStructureData, addNewCourseOpen, setAddNewCourseOpen, subscreenSelect, submitDepartment, reviewing, toggleReviewDepartment, changeDepartmentResource, setRolePages, showNextButton, handleMoveToNextScreen, setScreenAllowsUniversalSidebar, setUniversalSidebarLoadingInfo}) {
	const ref = useRef();

	const [addCourseOpen, setAddCourseOpen] = useState(false);
	const [courseResults, setCourseResults] = useState([...courses]);

	const [changesSavedMessage, setChangesSavedMessage] = useState(false);
	const [departmentCourses, setDepartmentCourses] = useState(null);
	const [currentView, setCurrentView] = useState('course');
	const [cleanDataSearchValue, setCleanDataSearchValue] = useState('');
	const [teacherCourseSearchOpen, setTeacherCourseSearchOpen] = useState(null);
	const [teacherCourseResultsByTeacher, setTeacherCourseResultsByTeacher] = useState({});
	const [pendingConnectionAdds, setPendingConnectionAdds] = useState([]);
	const [pendingConnectionRemoves, setPendingConnectionRemoves] = useState([]);

	/////////////////////////////
	///// UTILITY FUNCTIONS /////
	/////////////////////////////
	useOutsideClick(ref, () => {
		closeSupervisorsList();
	});

	const getConnectionKey = (course_id, teacher_id) => `${course_id}:${teacher_id}`;

	const addPendingConnectionAdd = (course_id, teacher_id) => {
		const connection_key = getConnectionKey(course_id, teacher_id);
		setPendingConnectionAdds(current_keys => current_keys.includes(connection_key) ? current_keys : [...current_keys, connection_key]);
	}

	const removePendingConnectionAdd = (course_id, teacher_id) => {
		const connection_key = getConnectionKey(course_id, teacher_id);
		setPendingConnectionAdds(current_keys => current_keys.filter(key => key !== connection_key));
	}

	const addPendingConnectionRemove = (course_id, teacher_id) => {
		const connection_key = getConnectionKey(course_id, teacher_id);
		setPendingConnectionRemoves(current_keys => current_keys.includes(connection_key) ? current_keys : [...current_keys, connection_key]);
	}

	const removePendingConnectionRemove = (course_id, teacher_id) => {
		const connection_key = getConnectionKey(course_id, teacher_id);
		setPendingConnectionRemoves(current_keys => current_keys.filter(key => key !== connection_key));
	}

	const isPendingConnectionAdd = (course_id, teacher_id) => pendingConnectionAdds.includes(getConnectionKey(course_id, teacher_id));

	const isPendingConnectionRemove = (course_id, teacher_id) => pendingConnectionRemoves.includes(getConnectionKey(course_id, teacher_id));

	const getPendingConnectionAddsForCourse = (course_id) => pendingConnectionAdds.filter(connection_key => connection_key.split(':')[0] === `${course_id}`);

	const getPendingConnectionAddsForTeacher = (teacher_id) => pendingConnectionAdds.filter(connection_key => connection_key.split(':')[1] === `${teacher_id}`);

	const textMatchesSearch = (searchable_text, search_value) => searchable_text.toLowerCase().includes(search_value);

	const addSearch = (e) => {
		const search_value = e.target.value;
		if(search_value.trim().length === 0)
		{
			setCourseResults([...courses]);
			return;
		}

		const fuse = new Fuse(courses, {
			keys: ['name', 'course_code'],
			threshold: .6
		})

		const results = fuse.search(search_value);
		const course_results = results.map(result => result.item);

		setCourseResults(course_results);
	}

	useEffect(() => {
		if(addCourseOpen) setCourseResults([...courses]);
	}, [addCourseOpen, courses]);

	////////////////////////////
	///// UPDATE FUNCTIONS /////
	////////////////////////////
	const updateCourseTeachers = async (course_id, update_type, teacher_id) => {
		if(update_type === 'add')
		{
			if(isPendingConnectionAdd(course_id, teacher_id)) return;
			addPendingConnectionAdd(course_id, teacher_id);
		}
		else if(update_type === 'remove')
		{
			if(isPendingConnectionRemove(course_id, teacher_id)) return;
			addPendingConnectionRemove(course_id, teacher_id);
		}

		const parentDiv = document.querySelectorAll(`.clean-data-connection-container[data-courseid='${course_id}'][data-teacherid='${teacher_id}']`)[0];

		const tct_type = (update_type === 'update') ? parentDiv.querySelector('.clean-data-connection-details').querySelector('.dropdown-display').getAttribute('data-value') : '1';

		const data = {school_id: schoolInfo.school_id, department_id:departmentID, schedule_version_id:schoolInfo.current_schedule_version_id, course_id:course_id, update_type:update_type, teacher_id:teacher_id, tct_type:tct_type};

		const apiName = process.env.REACT_APP_ENDPOINT_NAME;
		const url = '/admin/clean-data/update-course-teachers';
	    const myInit = { // OPTIONAL
	        response: true,
	        body: JSON.stringify(data),
	    };

		try {
			await API.post(apiName, url, myInit);

			///////////////////////
			///// UPDATE JSON /////
			///////////////////////
			const department_index = departments.findIndex(department => department.department_id === departmentID);

			if(userIsSupervisor)
			{
				if(department_index !== -1)
				{
					departments[department_index].subscreens[subscreen].submitted = false;
					departments[department_index].subscreens[subscreen].confirmed = false;
				}

				// Reset overall progress to update the Progress screen
				getData('schoolProgress', '/get-school-progress', {school_id: schoolInfo.school_id, schedule_version_id:schoolInfo.current_schedule_version_id}, true).then(user_progress_data => {
					const role_pages = user_progress_data.role_pages;

					setRolePages(role_pages);
				});
			}

			if(userIsAdmin && departments[department_index].subscreens[subscreen].confirmed) setChangesSavedMessage(true);

			const course_index = courses.findIndex(course => course.course_id === course_id);
			const course_info = courses[course_index];

			const teachers_can_teach = course_info.teachers_can_teach;

			if(update_type === 'remove')
			{
				const new_teachers_can_teach = teachers_can_teach.filter(tct_info => tct_info.teacher_id !== teacher_id);
				course_info.teachers_can_teach = new_teachers_can_teach;
			}
			else
			{
				// Check if course already has teacher attached
				// If yes, update type
				// If no, add teacher
				const teacher_index = teachers_can_teach.findIndex(tct_info => tct_info.teacher_id === teacher_id);

				if(teacher_index !== -1)
				{
					teachers_can_teach[teacher_index]['type'] = tct_type;
				}
				else
				{
					teachers_can_teach.push({teacher_id:teacher_id, type:tct_type});
				}
			}

			if(update_type !== 'add')
			{
				delete courses[course_index]['searching'];
			}
			setCourses([...courses]);
			setDepartments([...departments]);
			return true;
		} catch(e)
		{
			console.log(e.response);
			return false;
		} finally
		{
			if(update_type === 'add') removePendingConnectionAdd(course_id, teacher_id);
			else if(update_type === 'remove') removePendingConnectionRemove(course_id, teacher_id);
		}
	}

	const updateCourse = async (changeType, dataType, data_id, old_department_id, new_department_id) => {
		const changed = await changeDepartmentResource(changeType, dataType, data_id, old_department_id, new_department_id, () => {
			if(changeType === 'add' && userIsSupervisor)
			{
				const department_index = departments.findIndex(department => department.department_id === departmentID);

				if(department_index !== -1)
				{
					departments[department_index].subscreens[subscreen].submitted = false;
					departments[department_index].subscreens[subscreen].confirmed = false;
				}

				// Update cleaning screen progress (for check marks)
				departments[department_index].subscreens['course_availability'].submitted = false;
				departments[department_index].subscreens['course_availability'].confirmed = false;

				departments[department_index].subscreens['course_caps'].submitted = false;
				departments[department_index].subscreens['course_caps'].confirmed = false;

				departments[department_index].subscreens['course_lengths'].submitted = false;
				departments[department_index].subscreens['course_lengths'].confirmed = false;

				departments[department_index].subscreens['course_flags'].submitted = false;
				departments[department_index].subscreens['course_flags'].confirmed = false;

				departments[department_index].subscreens['course_teachers'].submitted = false;
				departments[department_index].subscreens['course_teachers'].confirmed = false;

				departments[department_index].subscreens['course_classrooms'].submitted = false;
				departments[department_index].subscreens['course_classrooms'].confirmed = false;

				setDepartments([...departments]);

				// Reset overall progress to update the Progress screen
				getData('schoolProgress', '/get-school-progress', {school_id: schoolInfo.school_id, schedule_version_id:schoolInfo.current_schedule_version_id}, true).then(user_progress_data => {
					const role_pages = user_progress_data.role_pages;

					setRolePages(role_pages);
				});
			}
		}, {
			onStart: () => startDepartmentResourceFeedback(changeType, data_id),
			onError: clearDepartmentResourceFeedback,
		});

		if(!changed) clearDepartmentResourceFeedback();
	}

	const toggleAddNewData = (course_id) => {
		const course_index = courses.findIndex(course => course.course_id === course_id);
		const course_info = courses[course_index];
		const opening_search = !('searching' in course_info);

		if(!opening_search)
		{
			delete courses[course_index]['searching'];
			delete courses[course_index]['department_teacher_results'];
			delete courses[course_index]['other_teacher_results'];
		}
		else
		{
			courses[course_index]['searching'] = true;
			course_info['department_teacher_results'] = teachers.filter(teacher => teacher.departments.includes(departmentID));
			course_info['other_teacher_results'] = teachers.filter(teacher => !teacher.departments.includes(departmentID));
		}

		setCourses([...courses]);
	}

	const fuzzySearch = (e, course_id) => {
		const search_value = e.target.value;
		const has_search_value = search_value.trim().length > 0;
		const course_index = courses.findIndex(course => course.course_id === course_id);
		const course_info = courses[course_index];
		let teacher_results = teachers;

		if(has_search_value)
		{
			const fuse = new Fuse(teachers, {
				keys: ['name', 'first_name'],
				threshold: .6
			})

			const results = fuse.search(search_value);
			teacher_results = results.map(result => result.item);
		}

		const department_teacher_results = teacher_results.filter(teacher => teacher.departments.includes(departmentID));
		const other_teacher_results = teacher_results.filter(teacher => !teacher.departments.includes(departmentID));

		course_info['department_teacher_results'] = has_search_value ? department_teacher_results.slice(0, 5) : department_teacher_results;
		course_info['other_teacher_results'] = has_search_value ? other_teacher_results.slice(0, 5) : other_teacher_results;
		setCourses([...courses]);
	}

	//////////////////////////////
	//// USE EFFECT FUNCTIONS ////
	//////////////////////////////
	useEffect(() => {
		if(!departmentID || !userInfo || !users || !departments) return;

		const department_index = departments.findIndex(department => department.department_id === departmentID);
		const department_info = (department_index !== -1) ? departments[department_index] : null;
		const department_supervisors = (department_info) ? department_info.supervisors : [];

		const available_users = users.filter(user => (user.role_id === '1' || user.role_id === '2') || department_supervisors.findIndex(supervisor => supervisor.user_id === user.user_id) !== -1);

		setScreenAllowsUniversalSidebar(true);
		setUniversalSidebarLoadingInfo({sidebar_name:"Course Teacher Notes", sidebar_type:'notes', subscreen:'course_teachers', department_id:departmentID, user_id:userInfo.user_id, available_users:available_users, help_link_path:`/app/${schoolType}/documentation/scheduling_wizard/edit_constraints/setting_constraints`});
	}, [departmentID, userInfo, users, departments, setScreenAllowsUniversalSidebar, setUniversalSidebarLoadingInfo]);

	useEffect(() => {
		if(!departmentID)
		{
			setDepartmentCourses([]);
			return;
		}

		setDepartmentCourses(courses.filter(course => course.departments.includes(departmentID)));
	}, [departmentID, courses]);

	const tctTypeOptions = [
		{value:'1', display: 'Primary'},
		{value:'2', display: 'Back Up'},
	];

	const viewOptions = [
		{value:'course', display: 'Course View'},
		{value:'teacher', display: 'Teacher View'},
	];

	const row_style = {gridTemplateColumns:'1fr 1fr auto', gridColumnGap:'15px'};

	const department_index = departments.findIndex(department => department.department_id === departmentID);
	const department_info = departments[department_index];
	const subscreen_info = (department_info) ? department_info.subscreens[subscreen] : null;

	const current_screen_submitted = (subscreen_info) ? subscreen_info.submitted : false;
	const current_screen_confirmed = (subscreen_info) ? subscreen_info.confirmed : false;
	const current_screen_unlocked= (subscreen_info) ? subscreen_info.unlocked : false;

	const show_current_screen = (current_screen_unlocked && (reviewing || userIsSupervisor || userIsAdmin));
	const screen_complete_for_user = ((current_screen_confirmed) || (userIsSupervisor && !userIsAdmin && current_screen_submitted));

	const department_courses = departmentCourses || [];
	const department_courses_ready = departmentCourses !== null;
	const { departmentResourceAddLoading, isDepartmentResourcePendingRemoval, startDepartmentResourceFeedback, clearDepartmentResourceFeedback } = useDepartmentResourceFeedback({ items: department_courses, dataIdKey:'course_id', dataAttribute:'data-courseid' });
	const getTeacherCourseResults = (teacher_id, search_value = '') => {
		if(search_value.trim().length === 0) return department_courses;

		const fuse = new Fuse(department_courses, {
			keys: ['name', 'course_code'],
			threshold: .6
		})

		const results = fuse.search(search_value);
		return results.map(result => result.item);
	}

	const toggleTeacherCourseSearch = (teacher_id) => {
		if(teacherCourseSearchOpen === teacher_id)
		{
			setTeacherCourseSearchOpen(null);
			return;
		}

		setTeacherCourseSearchOpen(teacher_id);
		setTeacherCourseResultsByTeacher({...teacherCourseResultsByTeacher, [teacher_id]: getTeacherCourseResults(teacher_id)});
	}

	const searchCoursesForTeacher = (e, teacher_id) => {
		setTeacherCourseResultsByTeacher({...teacherCourseResultsByTeacher, [teacher_id]: getTeacherCourseResults(teacher_id, e.target.value)});
	}

	const addCourseToTeacher = async (course_id, teacher_id) => {
		await updateCourseTeachers(course_id, 'add', teacher_id);
	}

	const department_course_teacher_ids = department_courses.reduce((result, course_info) => {
		const teachers_can_teach = Array.isArray(course_info.teachers_can_teach) ? course_info.teachers_can_teach : [];

		teachers_can_teach.forEach(tct_info => {
			if(teachers.findIndex(teacher => teacher.teacher_id === tct_info.teacher_id) !== -1) result.add(tct_info.teacher_id);
		});

		return result;
	}, new Set());
	const department_teacher_rows = teachers
		.filter(teacher => (Array.isArray(teacher.departments) && teacher.departments.includes(departmentID)) || department_course_teacher_ids.has(teacher.teacher_id))
		.map(teacher => {
			const teacher_courses = department_courses.reduce((result, course_info) => {
				const teachers_can_teach = Array.isArray(course_info.teachers_can_teach) ? course_info.teachers_can_teach : [];
				const tct_info = teachers_can_teach.find(tct => tct.teacher_id === teacher.teacher_id);

				if(tct_info) result.push({course_info, tct_info});

				return result;
			}, []);

			return {...teacher, teacher_courses};
		})
		.sort((teacher_a, teacher_b) => {
			const name_a = `${teacher_a.name || ''}, ${teacher_a.first_name || ''}`;
			const name_b = `${teacher_b.name || ''}, ${teacher_b.first_name || ''}`;
			return name_a.localeCompare(name_b);
		});
	const clean_data_search_value = cleanDataSearchValue.trim().toLowerCase();
	const filtered_department_courses = clean_data_search_value.length === 0 ? department_courses : department_courses.filter(course_info => {
		const teachers_can_teach = Array.isArray(course_info.teachers_can_teach) ? course_info.teachers_can_teach : [];
		const teacher_names = teachers_can_teach.reduce((result, tct_info) => {
			const teacher_info = teachers.find(teacher => teacher.teacher_id === tct_info.teacher_id);
			if(teacher_info) result.push(`${teacher_info.name || ''} ${teacher_info.first_name || ''}`);

			return result;
		}, []);
		const searchable_text = `${course_info.name || ''} ${course_info.course_code || ''} ${teacher_names.join(' ')}`;

		return textMatchesSearch(searchable_text, clean_data_search_value);
	});
	const filtered_department_teacher_rows = clean_data_search_value.length === 0 ? department_teacher_rows : department_teacher_rows.filter(teacher_info => {
		const course_names = teacher_info.teacher_courses.map(({course_info}) => `${course_info.name || ''} ${course_info.course_code || ''}`);
		const searchable_text = `${teacher_info.name || ''} ${teacher_info.first_name || ''} ${course_names.join(' ')}`;

		return textMatchesSearch(searchable_text, clean_data_search_value);
	});
	const visible_item_count = currentView === 'course' ? filtered_department_courses.length : filtered_department_teacher_rows.length;

	return (
		<>
		<h1 className='progress-main-header'>Course Teachers</h1>
		<p className='progress-subheader'>Set which teachers can teach each course.</p>
		{(subscreenRestricted) ?
		(
			<div className='general-screen-message'>
				<FontAwesomeIcon className='dark-blue-text' style={{fontSize:'60px'}} icon={faLock}/>
				<h1>No Access</h1>
				<div>You do not have access to this screen</div>
			</div>
		):(subscreenLocked) ?
		(
			<div className='general-screen-message'>
				<FontAwesomeIcon className='dark-blue-text' style={{fontSize:'60px'}} icon={faLock}/>
				<h1>Temporarily Locked</h1>
				<div>This screen is locked until all other prior steps are completed</div>
			</div>
		):(!departmentID) ?
		(
			<DepartmentProgressScreen subscreenResponsibleRoleID={subscreenResponsibleRoleID} subscreen={subscreen} sortedDepartments={sortedDepartments} changeDepartment={changeDepartment} subscreenSelect={subscreenSelect} userInfo={userInfo} userIsAdmin={userIsAdmin} userIsSupervisor={userIsSupervisor} />
		):(!userIsSupervisor) ?
		(
			<div className='general-screen-message'>
				<FontAwesomeIcon className='dark-blue-text' style={{fontSize:'60px'}} icon={faLock}/>
				<h1>No Access</h1>
				<div>You do not have access to change this department</div>
				<div className='blue-link' style={{marginTop:'10px'}} onClick={() => changeDepartment(null)}>Go back to department list</div>
			</div>
		):(departmentID) ?
			(
				<>
				<div id='clean-data-content'>
					<div>
						<FontAwesomeIcon icon={faChevronLeft} className='progress-back-to-departments-btn' onClick={()=>changeDepartment(null)}/>
					</div>
					<div className='clean-data-content-inner-content'>
						<div className='clean-data-submit-btn-container'>
							{!isLoading &&
								<>
								{(reviewing && screen_complete_for_user) &&
									<>
									{changesSavedMessage &&
										<div className='all-changes-saved-message' style={{margin:'15px 0px 0px 0px'}}>
											<FontAwesomeIcon className='green-text' icon={faCheckCircle}/>
											<div>
												<h5>Changes Saved</h5>
												<div className='small-text'>All your changes have been saved</div>
											</div>
											<FontAwesomeIcon className='x-remove' icon={faTimes} onClick={() => setChangesSavedMessage(false)}/>
										</div>
									}
									</>
								}
								{(!screen_complete_for_user && show_current_screen) &&
									<Button key={departmentID} classes={['mark-as-complete-btn']} selected={false} text={`${!current_screen_submitted ? 'Mark As Complete' : 'Confirm Department'}`} icon={faCheck} iconPosition='left' onClickFunction={() => submitDepartment(departmentID)} />
								}
								</>
							}
						</div>
						<div className='clean-data-content-header-container'>
							<h1 className='clean-data-content-header'>{department_info && department_info.department} Department</h1>
							{current_screen_confirmed ?
								(
									<div className='clean-data-status green-text'><div>Complete</div><FontAwesomeIcon icon={faCheckCircle}/></div>
								): (current_screen_submitted) ?
								(
									<div className='clean-data-status bright-yellow-text'><div>Pending Confirmation</div><FontAwesomeIcon icon={faCheckCircle}/></div>
								): (null)
							}
						</div>
						<div className='department-supervisor-parent-container'>
							<>
							{(department_info && department_info.supervisors.length === 0) &&
								<>
								<div className='department-supervisor-inner-container'>
									<FontAwesomeIcon className='department-supervisor-user-icon' icon={faUserCircle}/>
									<div className='department-supervisor-name'>No Supervisor Assigned</div>
								</div>
								</>
							}
							{department_info && department_info.supervisors.map((supervisor, index) => {
								return (
									<SupervisorPill
										supervisor={supervisor}
										canRemove={userIsAdmin}
										onRemove={() => removeSupervisorFromDepartment(supervisor.user_id)}
										key={index}
									/>
								)
							})}
							{userIsAdmin &&
								<>
								<SupervisorSelector
									triggerClassName='clean-data-add-supervisor-link small-text click-restricted'
									triggerText='Add a Supervisor'
									isEmpty={department_info && department_info.supervisors.length === 0}
									onOpen={() => openSupervisorsList(departmentID)}
									isOpen={department_info && department_info.supervisors_open}
									optionsRef={ref}
									supervisors={supervisors}
									onAddSupervisor={supervisor_info => addSupervisorToDepartment(supervisor_info)}
									onClose={() => closeSupervisorsList(departmentID)}
									settingsPath={`/app/${schoolType}/settings/users`}
									redirectPath={`/app/${schoolType}/progress/edit_constraints/course_teachers`}
								/>
								</>
							}
							</>
						</div>
						<div className='clean-data-container'>
							{isLoading ?
								(
									<div className="center-text">
										<img className='clean-data-loader' src={require(`../../../../images/balls.gif`)} alt='loading' />
									</div>
								):
								(
									<>
									{(!current_screen_unlocked) ?
										(
											<div className='general-screen-message'>
												<FontAwesomeIcon className='dark-blue-text' style={{fontSize:'60px'}} icon={faLock}/>
												<h1>Locked</h1>
												<div>This department has previous steps which have not been completed. Please review previous steps and make sure they are completed and saved. This page should open automatically once all previous steps have been finished.</div>
											</div>
										):(userIsAdmin && current_screen_unlocked && !show_current_screen && !current_screen_submitted && !userIsSupervisor) ?
										(
											<div className='general-screen-message'>
												<FontAwesomeIcon className='turquoise-text' style={{fontSize:'60px'}} icon={faHourglassHalf}/>
												<h1>Pending...</h1>
												<div>Waiting on confirmation from department supervisor</div>
												<p className='blue-link' onClick={toggleReviewDepartment}>Review course teachers yourself</p>
											</div>
										):(show_current_screen) ?
										(
											<>
											<div className='clean-data-options-row'>
												<div className='clean-data-search-bar-container'>
													<input
														className='clean-data-search-bar'
														value={cleanDataSearchValue}
														onChange={(e) => setCleanDataSearchValue(e.target.value)}
														placeholder={currentView === 'course' ? 'Search courses or teachers' : 'Search teachers or courses'}
													/>
													<FontAwesomeIcon className='clean-data-search-bar-icon' icon={faSearch}/>
													{cleanDataSearchValue.trim().length > 0 &&
														<FontAwesomeIcon className='clean-data-search-bar-clear' icon={faTimes} onClick={() => setCleanDataSearchValue('')}/>
													}
												</div>
												<div className='clean-data-change-view'>
													<Dropdown data={viewOptions} currentValue={currentView} displayDefault={`${capitalizeFirstLetters(currentView)} View`} handleChange={setCurrentView}/>
												</div>
											</div>
											{!department_courses_ready ?
												(
													<div className='center-text'>
														<img className='clean-data-loader' src={require(`../../../../images/balls.gif`)} alt='loading' />
													</div>
												) :
												department_courses.length === 0 ?
												(
													<div className='clean-data-no-data-container'>
														<FontAwesomeIcon className='clean-data-no-data-icon dark-blue-text' icon={faExclamationCircle}/>
														<div>No courses found</div>
														<p>Please confirm that this is correct by submitting the screen</p>
													</div>
												):
												clean_data_search_value.length > 0 && visible_item_count === 0 ?
												(
													<div className='clean-data-no-data-container'>
														<FontAwesomeIcon className='clean-data-no-data-icon dark-blue-text' icon={faExclamationCircle}/>
														<div>No matches found</div>
														<p>Try searching for a different {currentView === 'course' ? 'course or teacher' : 'teacher or course'}.</p>
													</div>
												):
												currentView === 'course' ?
												(
													<>
													{filtered_department_courses.map((course_info, i) => {
														const course_id = course_info.course_id;
														const teachers_can_teach = course_info.teachers_can_teach.filter(tct_info => teachers.findIndex(teacher => teacher.teacher_id === tct_info.teacher_id) !== -1);
														const pending_connection_adds = getPendingConnectionAddsForCourse(course_id);

														const is_searching = ('searching' in course_info) ? true : false;
														const removing_course = isDepartmentResourcePendingRemoval(course_id);

														const department_teacher_results = ('department_teacher_results' in course_info) ? course_info.department_teacher_results : [];
														const other_teacher_results = ('other_teacher_results' in course_info) ? course_info.other_teacher_results : [];
														const any_search_results = department_teacher_results.length + other_teacher_results.length;

														return (
															<div className='clean-data-connection-main-container' key={i} data-courseid={course_id}>
																{removing_course ?
																	<div className='center-text' style={{padding:'20px 0px'}}>
																		<img className='clean-data-loader' src={require(`../../../../images/balls.gif`)} alt='loading' />
																	</div>
																	:
																	<>
																		<h3 className='clean-data-connection-main-header'>
																			<div>{capitalizeFirstLetters(course_info.name)} ({course_info.course_code})</div>
																			<FontAwesomeIcon className='gray-to-red-link' icon={faTrashAlt} onClick={() => updateCourse('remove', 'course', course_id, departmentID, null)} style={{justifySelf:'center'}}/>
																		</h3>
																		<div className='clean-data-connection-main-container-inner'>
																			{teachers_can_teach.map((tct_info, index) => {
																				const teacher_id = tct_info.teacher_id;
																				const tct_type = tct_info.type;
																				const removing_connection = isPendingConnectionRemove(course_id, teacher_id);

																				const teacher_info = teachers.find(teacher => teacher.teacher_id === teacher_id);

																				return (
																					<div className='clean-data-connection-container' data-courseid={course_id} data-teacherid={teacher_id} style={row_style} key={index}>
																						<div className='clean-data-connection-header'>
																							<div className='dark-blue-text'><FontAwesomeIcon className='clean-data-connection-icon' icon={faChalkboardTeacher}/> {teacher_info.name}, {teacher_info.first_name}</div>
																						</div>
																						<div className='clean-data-connection-details'>
																							<Dropdown data={tctTypeOptions} currentValue={tct_type} handleChange={() => updateCourseTeachers(course_id, 'update', teacher_id)} />
																						</div>
																						<div className='clean-data-remove-connection-container' onClick={!removing_connection ? () => updateCourseTeachers(course_id, 'remove', teacher_id) : null}>
																							{removing_connection ?
																								<img className='clean-data-connection-loader' src={require(`../../../../images/balls.gif`)} alt='loading' />
																								:
																								<FontAwesomeIcon className='x-remove' icon={faTimes}/>
																							}
																						</div>
																					</div>
																				)
																			})}
																			{pending_connection_adds.map(connection_key => {
																				return (
																					<div className='clean-data-pending-connection-container' key={`pending-add-${connection_key}`}>
																						<img className='clean-data-connection-loader' src={require(`../../../../images/balls.gif`)} alt='loading' />
																					</div>
																				)
																			})}
																			<div className='blue-link clean-data-add-new-connection' onClick={() => toggleAddNewData(course_id)}>Add Teacher Who Can Teach</div>
																			{is_searching &&
																				<div className='search-add-row-container'>
																					<div className='search-add-search-container'>
																						<div className='search-add-adding-input-container'>
																							<input id={`search-input-course-${course_id}`} className='search-add-adding-input' onChange={(e) => fuzzySearch(e, course_id)} placeholder='Search for teacher' autoFocus={true}/>
																							<FontAwesomeIcon className='search-add-adding-input-icon' icon={faSearch}/>
																						</div>
																						<FontAwesomeIcon className='x-cancel search-add-cancel-new-btn' icon={faTimes} onClick={() => toggleAddNewData(course_id)}/>
																					</div>
																					{any_search_results > 0 &&
																						<div className='search-add-search-results'>
																							{(department_teacher_results.length !== 0) &&
																								<>
																								<div className='search-add-did-you-mean'>Department Teachers:</div>

																								{department_teacher_results.map((teacher, i) => {
																									const teacher_departments = teacher.departments.reduce((result, department_id) => {
																										const department_index = departments.findIndex(department => department.department_id === department_id);

																										if(department_index !== -1) result.push(departments[department_index].department);

																										return result;
																									}, []);

																									const department_text = teacher_departments.join(', ');
																									const teacher_selected = course_info.teachers_can_teach.some(tct_info => tct_info.teacher_id === teacher.teacher_id);

																									return (
																										<div className='search-add-search-result' key={i} style={{gridTemplateColumns:'1fr 1fr auto'}}>
																											<div>{teacher.name}, {teacher.first_name}</div>
																											<div className='dark-gray-text small-text'>{department_text}</div>
																											<div>
																												<SearchAddButton selected={teacher_selected} onAdd={() => updateCourseTeachers(course_id, 'add', teacher.teacher_id)} />
																											</div>
																										</div>
																									)
																								})}
																								</>
																							}
																							{(other_teacher_results.length !== 0) &&
																								<>
																								<div className='search-add-did-you-mean'>Other Teachers:</div>
																								{other_teacher_results.map((teacher, i) => {
																									const teacher_departments = teacher.departments.reduce((result, department_id) => {
																										const department_index = departments.findIndex(department => department.department_id === department_id);

																										if(department_index !== -1) result.push(departments[department_index].department);

																										return result;
																									}, []);

																									const department_text = teacher_departments.join(', ');
																									const teacher_selected = course_info.teachers_can_teach.some(tct_info => tct_info.teacher_id === teacher.teacher_id);

																									return (
																										<div className='search-add-search-result' key={i} style={{gridTemplateColumns:'1fr 1fr auto'}}>
																											<div>{teacher.name}, {teacher.first_name}</div>
																											<div className='dark-gray-text small-text'>{department_text}</div>
																											<div>
																												<SearchAddButton selected={teacher_selected} onAdd={() => updateCourseTeachers(course_id, 'add', teacher.teacher_id)} />
																											</div>
																										</div>
																									)
																								})}
																								</>
																							}
																						</div>
																					}
																				</div>
																			}
																		</div>
																	</>
																}
															</div>
														)
													})}
													</>
												):
												department_teacher_rows.length === 0 ?
												(
													<div className='clean-data-no-data-container'>
														<FontAwesomeIcon className='clean-data-no-data-icon dark-blue-text' icon={faExclamationCircle}/>
														<div>No teachers found</div>
														<p>No teachers are assigned to this department or attached to this department's courses yet.</p>
													</div>
												):
												(
													<>
													{filtered_department_teacher_rows.map((teacher_info, i) => {
														const teacher_id = teacher_info.teacher_id;
														const teacher_courses = teacher_info.teacher_courses;
														const is_searching = teacherCourseSearchOpen === teacher_id;
														const teacher_course_results = teacherCourseResultsByTeacher[teacher_id] || [];
														const pending_connection_adds = getPendingConnectionAddsForTeacher(teacher_id);

														return (
															<div className='clean-data-connection-main-container' key={i} data-teacherid={teacher_id}>
																<h3 className='clean-data-connection-main-header'>
																	<div>{teacher_info.name}, {teacher_info.first_name}</div>
																</h3>
																<div className='clean-data-connection-main-container-inner'>
																	{teacher_courses.length === 0 &&
																		<div className='small-text dark-gray-text' style={{margin:'0px 0px 10px 0px'}}>No courses assigned yet</div>
																	}
																	{teacher_courses.map(({course_info, tct_info}, index) => {
																		const course_id = course_info.course_id;
																		const tct_type = tct_info.type;
																		const removing_connection = isPendingConnectionRemove(course_id, teacher_id);

																		return (
																			<div className='clean-data-connection-container' data-courseid={course_id} data-teacherid={teacher_id} style={row_style} key={index}>
																				<div className='clean-data-connection-header'>
																					<div className='dark-blue-text'><FontAwesomeIcon className='clean-data-connection-icon' icon={faBook}/> {capitalizeFirstLetters(course_info.name)} ({course_info.course_code})</div>
																				</div>
																				<div className='clean-data-connection-details'>
																					<Dropdown data={tctTypeOptions} currentValue={tct_type} handleChange={() => updateCourseTeachers(course_id, 'update', teacher_id)} />
																				</div>
																				<div className='clean-data-remove-connection-container' onClick={!removing_connection ? () => updateCourseTeachers(course_id, 'remove', teacher_id) : null}>
																					{removing_connection ?
																						<img className='clean-data-connection-loader' src={require(`../../../../images/balls.gif`)} alt='loading' />
																						:
																						<FontAwesomeIcon className='x-remove' icon={faTimes}/>
																					}
																				</div>
																			</div>
																		)
																	})}
																	{pending_connection_adds.map(connection_key => {
																		return (
																			<div className='clean-data-pending-connection-container' key={`pending-add-${connection_key}`}>
																				<img className='clean-data-connection-loader' src={require(`../../../../images/balls.gif`)} alt='loading' />
																			</div>
																		)
																	})}
																	<div className='blue-link clean-data-add-new-connection' onClick={() => toggleTeacherCourseSearch(teacher_id)}>Add Course They Can Teach</div>
																	{is_searching &&
																		<div className='search-add-row-container'>
																			<div className='search-add-search-container'>
																				<div className='search-add-adding-input-container'>
																					<input id={`search-input-teacher-${teacher_id}`} className='search-add-adding-input' onChange={(e) => searchCoursesForTeacher(e, teacher_id)} placeholder='Search for course' autoFocus={true}/>
																					<FontAwesomeIcon className='search-add-adding-input-icon' icon={faSearch}/>
																				</div>
																				<FontAwesomeIcon className='x-cancel search-add-cancel-new-btn' icon={faTimes} onClick={() => toggleTeacherCourseSearch(teacher_id)}/>
																			</div>
																			{teacher_course_results.length !== 0 &&
																				<div className='search-add-search-results'>
																					<div className='search-add-did-you-mean'>Department Courses:</div>
																					{teacher_course_results.map((course, i) => {
																						const course_selected = (course.teachers_can_teach || []).some(tct_info => tct_info.teacher_id === teacher_id);
																						return (
																							<div className='search-add-search-result' key={i} style={{gridTemplateColumns:'1fr auto'}}>
																								<div>{capitalizeFirstLetters(course.name)} ({course.course_code})</div>
																								<div>
																									<SearchAddButton selected={course_selected} onAdd={() => addCourseToTeacher(course.course_id, teacher_id)} />
																								</div>
																							</div>
																						)
																					})}
																				</div>
																			}
																		</div>
																	}
																</div>
															</div>
														)
													})}
													</>
												)
											}
											{departmentResourceAddLoading &&
												<div className='center-text' style={{padding:'10px 0px 0px 0px'}}>
													<img className='clean-data-loader' src={require(`../../../../images/balls.gif`)} alt='loading' />
												</div>
											}
											<div className='clean-data-add-new-data blue-link assign-resources-supervisor-confirm-add-new' onClick={() => setAddCourseOpen(!addCourseOpen)}>Add A Course</div>
											{addCourseOpen &&
												<div className='search-add-row-container'>
													<div className='search-add-search-container'>
														<div className='search-add-adding-input-container'>
															<input id='teacher-search-input' className='search-add-adding-input' onChange={(e) => addSearch(e)} placeholder='Search for course'/>
															<FontAwesomeIcon className='search-add-adding-input-icon' icon={faSearch}/>
														</div>
														<FontAwesomeIcon className='x-cancel search-add-cancel-new-btn' icon={faTimes} onClick={() => setAddCourseOpen(!addCourseOpen)}/>
													</div>
													{courseResults.length !== 0 &&
														<div className='search-add-search-results'>
															<div className='search-add-did-you-mean'>Did you mean:</div>
															{courseResults.map((course, i) => {
																const course_selected = course.departments.includes(departmentID);
																return (
																	<div className='search-add-search-result' key={i} style={{gridTemplateColumns:'1fr auto'}}>
																		<div>{capitalizeFirstLetters(course.name)} ({course.course_code})</div>
																		<div>
																			<SearchAddButton selected={course_selected} onAdd={() => updateCourse('add', 'course', course.course_id, null, departmentID)} />
																		</div>
																	</div>
																)
															})}
															<div className='clean-data-add-new-data-text dark-blue-text'>If you don&rsquo;t see the course you are looking for, you should first add them <span className='blue-link' onClick={() => setAddNewCourseOpen(true)}>Add A New Course</span></div>
														</div>
													}
												</div>
											}
											{addNewCourseOpen &&
												<EditCourseData schoolInfo={schoolInfo} dataID={null} toggleEditData={() => setAddNewCourseOpen(false)} scheduleStructureData={scheduleStructureData} scheduleInfo={scheduleInfo} courses={courses} students={students} classrooms={classrooms} teachers={teachers} departments={departments} setDatabaseData={setCourses} setStudents={setStudents} sections={sections} labels={labels} defaultDepartmentID={departmentID}/>
											}
											</>
										):null
									}
									</>
								)
							}
						</div>
					</div>
				</div>
				</>
			):null}
			{showNextButton &&
				<div className='fixed-bottom-save-container'>
					<Button classes={['btn', 'btn-extra-large', 'green-btn', 'align-right', 'move-to-next-screen-btn']} selected={false} text='Go to Next Step: Classroom Settings' onClickFunction={handleMoveToNextScreen} />
				</div>
			}
		</>
	);
}
